// Mesh utility functions
import type { Vec2, Edge } from '../types';
import { PAD, INNER_MARGIN_N } from '../constants';

// Math utilities
export const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
export const rand = (min = 0, max = 1) => Math.random() * (max - min) + min;
export const dist2 = (p: Vec2, q: Vec2) => (p.x - q.x) ** 2 + (p.y - q.y) ** 2;
export const dist = (p: Vec2, q: Vec2) => Math.sqrt(dist2(p, q));

// Coordinate conversion utilities
export const toPxX = (xn: number, W: number) => PAD + xn * Math.max(1, W - 2 * PAD);
export const toPxY = (yn: number, H: number) => PAD + yn * Math.max(1, H - 2 * PAD);
export const toNormX = (xp: number, W: number) => clamp((xp - PAD) / Math.max(1, W - 2 * PAD), 0, 1);
export const toNormY = (yp: number, H: number) => clamp((yp - PAD) / Math.max(1, H - 2 * PAD), 0, 1);

// Build radius-based topology: connect all nodes within range (realistic)
export function buildRadiusTopology(nodes: Vec2[], maxRange: number): Edge[] {
  const edges: Edge[] = [];
  const edgeSet = new Set<string>();
  
  if (nodes.length < 2) return edges;
  
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const d = dist(nodes[i], nodes[j]);
      if (d <= maxRange) {
        const edgeKey = `${i}-${j}`;
        if (!edgeSet.has(edgeKey)) {
          edges.push({ a: i, b: j, len: d });
          edgeSet.add(edgeKey);
        }
      }
    }
  }
  
  return edges;
}

// Build K-Nearest Neighbors mesh topology with organic variations
export function buildKNN(nodes: Vec2[], k = 3, maxDist = 0.25): Edge[] {
  const edges: Edge[] = [];
  if (nodes.length < 2) return edges;
  
  for (let i = 0; i < nodes.length; i++) {
    // Vary K per node for organic topology (some hubs, some sparse)
    const kVariation = Math.floor(rand(-1, 2)); // -1, 0, or 1
    const kNode = Math.max(1, Math.min(k + kVariation, nodes.length - 1));
    
    const dists = nodes.map((p, j) => ({ j, d2: i === j ? Infinity : dist2(nodes[i], p) }));
    dists.sort((a, b) => a.d2 - b.d2);
    
    for (let n = 0; n < kNode; n++) {
      const d = Math.sqrt(dists[n].d2);
      
      // Skip if distance exceeds threshold (organic cutoff)
      if (d > maxDist) continue;
      
      const j = dists[n].j;
      const a = Math.min(i, j);
      const b = Math.max(i, j);
      const exists = edges.some((e) => e.a === a && e.b === b);
      if (!exists) edges.push({ a, b, len: d });
    }
    
    // Occasionally add a random "long distance" connection for interest
    if (Math.random() < 0.05 && dists.length > kNode + 5) {
      const longIdx = Math.floor(rand(kNode + 3, Math.min(dists.length, kNode + 15)));
      const j = dists[longIdx].j;
      const d = Math.sqrt(dists[longIdx].d2);
      if (d <= maxDist * 1.5) { // Slightly relaxed for long connections
        const a = Math.min(i, j);
        const b = Math.max(i, j);
        const exists = edges.some((e) => e.a === a && e.b === b);
        if (!exists) edges.push({ a, b, len: d });
      }
    }
  }
  return edges;
}

// Poisson disk sampling for organic, well-spaced distribution
function poissonDiskSampling(count: number, minDist: number, maxAttempts = 30): Vec2[] {
  const nodes: Vec2[] = [];
  const cellSize = minDist / Math.sqrt(2);
  const gridW = Math.ceil(1 / cellSize);
  const gridH = Math.ceil(1 / cellSize);
  const grid: (Vec2 | null)[][] = Array(gridH).fill(null).map(() => Array(gridW).fill(null));
  const active: Vec2[] = [];
  
  // Define margin to prevent edge clustering
  const margin = 0.05;

  // Helper to get grid cell
  const getCell = (p: Vec2) => ({
    x: Math.floor(p.x / cellSize),
    y: Math.floor(p.y / cellSize),
  });

  // Helper to check if point is valid
  const isValid = (p: Vec2): boolean => {
    // Keep points away from edges
    if (p.x < margin || p.x > 1 - margin || p.y < margin || p.y > 1 - margin) return false;
    
    const cell = getCell(p);
    const searchRadius = 2;
    
    for (let dy = -searchRadius; dy <= searchRadius; dy++) {
      for (let dx = -searchRadius; dx <= searchRadius; dx++) {
        const nx = cell.x + dx;
        const ny = cell.y + dy;
        if (nx >= 0 && nx < gridW && ny >= 0 && ny < gridH) {
          const neighbor = grid[ny][nx];
          if (neighbor && dist(p, neighbor) < minDist) {
            return false;
          }
        }
      }
    }
    return true;
  };

  // Add point to grid
  const addPoint = (p: Vec2) => {
    nodes.push(p);
    active.push(p);
    const cell = getCell(p);
    if (cell.y < gridH && cell.x < gridW) {
    grid[cell.y][cell.x] = p;
    }
  };

  // Start with multiple seed points distributed across the area (decentralized)
  const numSeeds = Math.min(5, Math.ceil(count / 20));
  for (let i = 0; i < numSeeds; i++) {
    let attempts = 0;
    let seedPoint: Vec2;
    do {
      // Distribute seeds across the entire valid area
      seedPoint = { 
        x: rand(margin + 0.1, 1 - margin - 0.1), 
        y: rand(margin + 0.1, 1 - margin - 0.1) 
      };
      attempts++;
    } while (!isValid(seedPoint) && attempts < 50);
    
    if (isValid(seedPoint)) {
      addPoint(seedPoint);
    }
  }

  // Generate points
  while (active.length > 0 && nodes.length < count) {
    const idx = Math.floor(rand(0, active.length));
    const point = active[idx];
    let found = false;

    for (let i = 0; i < maxAttempts; i++) {
      const angle = rand(0, Math.PI * 2);
      const radius = rand(minDist, minDist * 2);
      const newPoint = {
        x: point.x + Math.cos(angle) * radius,
        y: point.y + Math.sin(angle) * radius,
      };

      if (isValid(newPoint)) {
        addPoint(newPoint);
        found = true;
        break;
      }
    }

    if (!found) {
      active.splice(idx, 1);
    }
  }

  // If we don't have enough nodes, add random ones with distance check
  let fillAttempts = 0;
  while (nodes.length < count && fillAttempts < count * 50) {
    const newPoint: Vec2 = { 
      x: rand(margin, 1 - margin), 
      y: rand(margin, 1 - margin) 
    };
    
    if (isValid(newPoint)) {
      addPoint(newPoint);
    }
    fillAttempts++;
  }
  
  // Last resort: add points with relaxed constraints if still not enough
  while (nodes.length < count) {
    nodes.push({ 
      x: rand(margin, 1 - margin), 
      y: rand(margin, 1 - margin) 
    });
  }

  return nodes;
}

// Generate nodes with organic distribution
export function seededNodes(count: number): Vec2[] {
  // Calculate minimum distance based on node count for good spacing
  const area = 0.81; // Effective area (0.9 * 0.9) accounting for margins
  const minDist = Math.sqrt(area / count) * 0.6; // Balanced spacing
  
  // Use Poisson disk sampling for organic distribution
  const nodes = poissonDiskSampling(count, minDist);
  
  // Very light relaxation to smooth out any remaining issues
  return jitterRelax(nodes, 5, 0.0008);
}

// Lightweight relaxation for final smoothing
export function jitterRelax(nodes: Vec2[], iterations = 8, step = 0.001): Vec2[] {
  const pts = nodes.map((n) => ({ ...n }));
  
  for (let it = 0; it < iterations; it++) {
    for (let i = 0; i < pts.length; i++) {
      let fx = 0, fy = 0;
      
      // Very light repulsion from nearby nodes only
      for (let j = 0; j < pts.length; j++) {
        if (i === j) continue;
        const dx = pts[i].x - pts[j].x;
        const dy = pts[i].y - pts[j].y;
        const d2 = dx * dx + dy * dy + 1e-6;
        
        // Only apply force for very close neighbors
        if (d2 < 0.01) {
          const inv = 1 / d2;
          fx += dx * inv * 0.5;
          fy += dy * inv * 0.5;
        }
      }

      // Apply movement with strong damping
      const damping = 0.8;
      pts[i].x = clamp(pts[i].x + fx * step * damping, 0, 1);
      pts[i].y = clamp(pts[i].y + fy * step * damping, 0, 1);
    }
  }
  
  return pts;
}

// Clamp coordinates within inner margin for click placement
export const clampToInnerMargin = (x: number, y: number): Vec2 => ({
  x: clamp(x, INNER_MARGIN_N, 1 - INNER_MARGIN_N),
  y: clamp(y, INNER_MARGIN_N, 1 - INNER_MARGIN_N),
});

// Simple 2D Perlin-like noise for organic node drift
class SimpleNoise {
  private p: number[];
  
  constructor(seed = 0) {
    // Generate permutation table
    this.p = [];
    for (let i = 0; i < 256; i++) {
      this.p[i] = i;
    }
    
    // Shuffle with seed
    let s = seed;
    for (let i = 255; i > 0; i--) {
      s = (s * 9301 + 49297) % 233280;
      const j = Math.floor((s / 233280) * (i + 1));
      [this.p[i], this.p[j]] = [this.p[j], this.p[i]];
    }
    
    // Duplicate for wrapping
    for (let i = 0; i < 256; i++) {
      this.p[256 + i] = this.p[i];
    }
  }
  
  private fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }
  
  private lerp(t: number, a: number, b: number): number {
    return a + t * (b - a);
  }
  
  private grad(hash: number, x: number, y: number): number {
    const h = hash & 3;
    const u = h < 2 ? x : y;
    const v = h < 2 ? y : x;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
  }
  
  noise(x: number, y: number): number {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    
    x -= Math.floor(x);
    y -= Math.floor(y);
    
    const u = this.fade(x);
    const v = this.fade(y);
    
    const a = this.p[X] + Y;
    const aa = this.p[a];
    const ab = this.p[a + 1];
    const b = this.p[X + 1] + Y;
    const ba = this.p[b];
    const bb = this.p[b + 1];
    
    return this.lerp(
      v,
      this.lerp(u, this.grad(this.p[aa], x, y), this.grad(this.p[ba], x - 1, y)),
      this.lerp(u, this.grad(this.p[ab], x, y - 1), this.grad(this.p[bb], x - 1, y - 1))
    );
  }
}

// Global noise instance for consistent drift
const noiseGen = new SimpleNoise(12345);

// Apply gentle drift to node positions based on time
export function applyNodeDrift(nodes: Vec2[], time: number, driftAmount = 0.008): Vec2[] {
  return nodes.map((node, i) => {
    // Each node has its own frequency offset for varied motion
    const offsetX = i * 10.7;
    const offsetY = i * 13.3;
    const freq = 0.3;
    
    const driftX = noiseGen.noise(offsetX + time * freq, offsetY) * driftAmount;
    const driftY = noiseGen.noise(offsetX, offsetY + time * freq) * driftAmount;
    
    return {
      x: clamp(node.x + driftX, 0, 1),
      y: clamp(node.y + driftY, 0, 1),
    };
  });
}

// Generate a routing path using flood routing (Meshtastic-style)
// Packets hop through intermediate nodes rather than traveling direct
// Meshtastic uses hop_limit (default 3, max 7) that decrements at each retransmission
export function generateFloodPath(
  edges: { a: number; b: number; len: number }[],
  nodeCount: number,
  maxHops = 7 // Meshtastic max hop_limit is 7
): number[] {
  if (nodeCount < 2 || edges.length === 0) return [];
  
  // Pick random start and end nodes
  let start = Math.floor(rand(0, nodeCount));
  let end = Math.floor(rand(0, nodeCount));
  
  // Ensure start and end are different
  if (start === end) {
    end = (end + 1) % nodeCount;
  }
  
  // Build adjacency list from edges
  const adj: number[][] = Array(nodeCount).fill(null).map(() => []);
  for (const edge of edges) {
    // Ensure indices are valid
    if (edge.a >= 0 && edge.a < nodeCount && edge.b >= 0 && edge.b < nodeCount) {
      adj[edge.a].push(edge.b);
      adj[edge.b].push(edge.a);
    }
  }
  
  // Ensure start node has connections
  if (adj[start].length === 0) {
    // Find a node with connections
    for (let i = 0; i < nodeCount; i++) {
      if (adj[i].length > 0) {
        start = i;
        break;
      }
    }
  }
  
  // BFS to find path (simulating flood routing)
  const queue: { node: number; path: number[] }[] = [{ node: start, path: [start] }];
  const visited = new Set<number>([start]);
  
  while (queue.length > 0) {
    const { node, path } = queue.shift()!;
    
    // Found destination
    if (node === end) {
      // Validate entire path has valid edges
      for (let i = 0; i < path.length - 1; i++) {
        const from = path[i];
        const to = path[i + 1];
        const hasEdge = edges.some(
          (e) => (e.a === from && e.b === to) || (e.a === to && e.b === from)
        );
        if (!hasEdge) {
          // Path is invalid, return empty
          return [];
        }
      }
      return path.slice(0, maxHops);
    }
    
    // Don't exceed max hops
    if (path.length >= maxHops) {
      continue;
    }
    
    // Add neighbors (only those connected by actual edges)
    const neighbors = adj[node] || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor) && neighbor >= 0 && neighbor < nodeCount) {
        // Double-check edge exists
        const hasEdge = edges.some(
          (e) => (e.a === node && e.b === neighbor) || (e.a === neighbor && e.b === node)
        );
        if (hasEdge) {
          visited.add(neighbor);
          queue.push({ node: neighbor, path: [...path, neighbor] });
        }
      }
    }
  }
  
  // No valid path found
  return [];
}